import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabase'
import { STATUTS_SAISON, collecteOuverte, jourFerme, formatLong, fetchAll } from '../../utils/planning'

function formatHeure(ts) {
  if (!ts) return '-'
  const d = new Date(ts)
  const p = n => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
}

export default function CampagneDispos() {
  const [saisons, setSaisons] = useState([])
  const [saisonId, setSaisonId] = useState(null)
  const [form, setForm] = useState({ statut: 'collecte', dispos_ouverture: '', dispos_cloture: '' })
  const [lignes, setLignes] = useState([])
  const [nbOuverts, setNbOuverts] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  const saison = saisons.find(s => s.id === saisonId) || null

  const chargerSaisons = useCallback(async () => {
    const { data, error } = await supabase.from('planning_saisons').select('*').order('date_debut', { ascending: false })
    if (error) {
      setMsg({ type: 'error', text: 'Impossible de charger les saisons.' })
      setLoading(false)
      return
    }
    setSaisons(data || [])
    setSaisonId(prev => prev || ((data || []).find(s => s.statut === 'collecte') || (data || [])[0] || {}).id || null)
    if (!data || data.length === 0) setLoading(false)
  }, [])

  useEffect(() => { chargerSaisons() }, [chargerSaisons])

  useEffect(() => {
    if (saison) {
      setForm({
        statut: saison.statut,
        dispos_ouverture: saison.dispos_ouverture || '',
        dispos_cloture: saison.dispos_cloture || '',
      })
    }
  }, [saison])

  const chargerReponses = useCallback(async () => {
    if (!saison) return
    setLoading(true)
    const [rSup, rJours, rDispos] = await Promise.all([
      supabase.from('profiles').select('id, prenom, nom').eq('role', 'superviseur').eq('actif', true).order('prenom'),
      fetchAll(() => supabase.from('jours_planning')
        .select('date, accueil_ouvert, supervision_ouverte')
        .gte('date', saison.date_debut).lte('date', saison.date_fin).order('date')),
      fetchAll(() => supabase.from('dispos_annuelles')
        .select('superviseur_id, date, dispo, updated_at')
        .gte('date', saison.date_debut).lte('date', saison.date_fin)
        .order('superviseur_id').order('date')),
    ])
    if (rSup.error || rJours.error || rDispos.error) {
      setMsg({ type: 'error', text: 'Erreur lors du chargement des réponses.' })
    }
    const ouverts = new Set(rJours.data.filter(j => !jourFerme(j)).map(j => j.date))
    const parSup = {}
    for (const d of rDispos.data) {
      if (!ouverts.has(d.date)) continue
      if (!parSup[d.superviseur_id]) parSup[d.superviseur_id] = { oui: 0, non: 0, maj: '' }
      const l = parSup[d.superviseur_id]
      if (d.dispo) l.oui += 1
      else l.non += 1
      if (d.updated_at > l.maj) l.maj = d.updated_at
    }
    setNbOuverts(ouverts.size)
    setLignes((rSup.data || []).map(s => {
      const c = parSup[s.id] || { oui: 0, non: 0, maj: '' }
      return { ...s, oui: c.oui, non: c.non, restant: ouverts.size - c.oui - c.non, maj: c.maj }
    }))
    setLoading(false)
  }, [saison])

  useEffect(() => { chargerReponses() }, [chargerReponses])

  async function enregistrer(e) {
    e.preventDefault()
    setMsg(null)
    if (form.dispos_ouverture && form.dispos_cloture && form.dispos_cloture < form.dispos_ouverture) {
      setMsg({ type: 'error', text: "La date de clôture est antérieure à la date d'ouverture." })
      return
    }
    setSaving(true)
    const { error } = await supabase.from('planning_saisons').update({
      statut: form.statut,
      dispos_ouverture: form.dispos_ouverture || null,
      dispos_cloture: form.dispos_cloture || null,
    }).eq('id', saison.id)
    setSaving(false)
    if (error) {
      setMsg({ type: 'error', text: "Erreur lors de l'enregistrement." })
      return
    }
    setMsg({ type: 'success', text: 'Campagne mise à jour.' })
    chargerSaisons()
  }

  if (!loading && !saison) {
    return (
      <div>
        <div className="page-header"><div><h2>Campagne de disponibilités</h2></div></div>
        {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}
        <div className="card" style={{ textAlign: 'center', color: 'var(--stone)', padding: 32 }}>
          Aucune saison de planning. Exécute d'abord la migration supabase-planning.sql.
        </div>
      </div>
    )
  }

  const ouverte = collecteOuverte(saison)
  const complets = lignes.filter(l => l.restant === 0).length
  const aucune = lignes.filter(l => l.oui + l.non === 0).length

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Campagne de disponibilités</h2>
          <div className="page-header-sub">
            {saison ? `Saison ${saison.libelle} : ${formatLong(saison.date_debut)} au ${formatLong(saison.date_fin)}` : ''}
          </div>
        </div>
        {saisons.length > 1 && (
          <select value={saisonId || ''} onChange={e => setSaisonId(e.target.value)}>
            {saisons.map(s => <option key={s.id} value={s.id}>{s.libelle}</option>)}
          </select>
        )}
      </div>

      {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}

      {saison && (
        <div className="card" style={{ marginBottom: 20 }}>
          <form onSubmit={enregistrer}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 20px' }}>
              <div className="form-group">
                <label>Étape de la saison</label>
                <select value={form.statut} onChange={e => setForm(f => ({ ...f, statut: e.target.value }))}>
                  {Object.entries(STATUTS_SAISON).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Ouverture de la saisie (facultatif)</label>
                <input type="date" value={form.dispos_ouverture} onChange={e => setForm(f => ({ ...f, dispos_ouverture: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Clôture de la saisie (facultatif)</label>
                <input type="date" value={form.dispos_cloture} onChange={e => setForm(f => ({ ...f, dispos_cloture: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="submit" className="btn btn-bordeaux" disabled={saving}>
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
              <span className={ouverte ? 'badge badge-actif' : 'badge badge-warning'}>
                {ouverte ? 'Saisie ouverte aux superviseurs' : 'Saisie fermée aux superviseurs'}
              </span>
            </div>
            <div className="text-muted" style={{ fontSize: 12, marginTop: 12 }}>
              Les superviseurs ne peuvent saisir leurs disponibilités que lorsque l'étape est "Collecte des disponibilités"
              et que la date du jour est dans la fenêtre d'ouverture et de clôture. Passer à "Construction du planning"
              verrouille la saisie.
            </div>
          </form>
        </div>
      )}

      {loading ? <div className="spinner" /> : (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="s-label">Superviseurs actifs</div>
              <div className="s-value">{lignes.length}</div>
            </div>
            <div className="stat-card">
              <div className="s-label">Réponses complètes</div>
              <div className="s-value text-success">{complets}</div>
              <div className="s-sub">{nbOuverts} jours ouverts à renseigner</div>
            </div>
            <div className="stat-card">
              <div className="s-label">Aucune réponse</div>
              <div className="s-value text-bordeaux">{aucune}</div>
            </div>
          </div>

          <div className="card">
            <table>
              <thead>
                <tr>
                  <th>Superviseur</th>
                  <th className="num">Disponible</th>
                  <th className="num">Indisponible</th>
                  <th className="num">À renseigner</th>
                  <th>Statut</th>
                  <th>Dernière modification</th>
                </tr>
              </thead>
              <tbody>
                {lignes.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--stone)', padding: 24 }}>Aucun superviseur actif</td></tr>
                ) : lignes.map(l => (
                  <tr key={l.id}>
                    <td><strong>{l.prenom} {l.nom}</strong></td>
                    <td className="num">{l.oui}</td>
                    <td className="num">{l.non}</td>
                    <td className="num">{l.restant}</td>
                    <td>
                      {l.restant === 0
                        ? <span className="badge badge-actif">Complet</span>
                        : l.oui + l.non === 0
                          ? <span className="badge badge-inactif">Aucune réponse</span>
                          : <span className="badge badge-warning">Partiel</span>}
                    </td>
                    <td className="text-muted">{formatHeure(l.maj)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
